import { FetchError } from '../../common/api/api';
import { Attachment } from '../../common/attachment/Attachment.interface';
import { User } from '../../user/User.interface';
import { ChangeOrder } from '../changeOrder/ChangeOrder.interface';
import { DueStatus } from '../dashboard/components/DueStatusLabel';
import { Group } from '../groups/Group.interface';
import { Task } from '../tasks/Task.interface';

export interface TrainingSection {
  id?: number;
  name: string;
  body: string;
  url?: string;
  fileId?: number;
  fileVersion?: number;
  fileUpload?: {
    enabled?: boolean;
    label?: string;
    description?: string;
    fileType?: string;
    multiple?: boolean;
    minRequired?: number;
  };
}

export interface TrainingSectionAttachmentProgress {
  index: number;
  total: number;
  min: number;
  isValid: boolean;
}

export interface TrainingStatusLog {
  id: number;
  version: number;
  created: Date;
  createdBy: User;
}

export interface TrainingFileMetadata {
  // task
  taskId: number;
  taskKey: string;
  taskVersion: number;
  // fileVersion
  id: number;
  name: string;
  fileVersion: number; // fileVersion.version
  storageKey: string;
  // display only, this value is computed from taskVersion and fileVersion
  version: number;
}

export interface Training {
  id: number;
  taskId: number;
  task: Task;
  name: string;
  description?: string;
  frequency: number | null;
  version: number;
  assignedDate: Date;
  dueDate: Date;
  sections: TrainingSection[];
  groups?: Group[];
  changeOrders?: ChangeOrder[];
  fileMetadata?: TrainingFileMetadata[];
}

export interface TrainingVersion {
  id: number;
  trainingId: number;
  training?: Training;
  name: string;
  version: string;
  sections?: TrainingSection[];
  createdBy: User;
  created: Date;
  fileMetadata?: TrainingFileMetadata[];
}

export interface TrainingGroupDto {
  trainingId: number;
  groupId: number;
  groupName: string;
  completedCount: number;
  inProgressCount: number;
  toDoCount: number;
}

export interface TrainingUserAttachment extends Attachment {
  sectionIndex: number;
}

export enum TrainingUserStatus {
  ToDo = 'to-do',
  InProgress = 'in-progress',
  Completed = 'completed'
}

export interface TrainingUser {
  id: number;
  status: TrainingUserStatus;
  assignedDate: Date;
  dueDate?: Date;
  startDate?: Date;
  completedDate?: Date;
  dueStatus?: DueStatus;
  trainingVersion: Partial<TrainingVersion>;
  userId: number;
  user: Partial<User>;
  groups?: Partial<Group>[];
  attachments: TrainingUserAttachment[];
}

export interface TrainingUpdateDto extends Training {
  shouldUpdateStatus?: boolean;
}

export enum TrainingTransition {
  Approve = 'approve',
  Revert = 'revert',
  Transition = 'transition'
}

export interface BaseTrainingCardProps {
  training: Training;
  onChange?: (training: Partial<Training>) => void;
  onUpdate?: () => void;
  onError?: (error: FetchError) => void;
}

export enum TrainingReminderType {
  Incomplete = 'incomplete',
  Overdue = 'overdue'
}

export interface TrainingSendReminderDto {
  type: TrainingReminderType;
  subject: string;
  message: string;
}

export interface TrainingSendReminderResponse {
  success: number;
  error: number;
  total: number;
}
